import React, {useState, useRef, useEffect} from 'react'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'

interface CustomCalendarProps {
  value?: string
  onChange?: (date: string) => void
  placeholder?: string
  label?: React.ReactNode
  required?: boolean
  disabled?: boolean
  maxDate?: Date
  minDate?: Date
  className?: string
  error?: string
  onFocus?: () => void
  onBlur?: () => void
}

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  value = '',
  onChange,
  placeholder = 'Pilih Tanggal',
  label = 'Tanggal',
  required = false,
  disabled = false,
  maxDate,
  minDate,
  className = '',
  error,
  onFocus,
  onBlur,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [calendarPosition, setCalendarPosition] = useState<any>({})
  const [initialInputRect, setInitialInputRect] = useState<DOMRect | null>(null)
  const calendarRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLDivElement>(null)

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsCalendarOpen(false)
        onBlur?.()
      }
    }

    const handleResize = () => {
      if (isCalendarOpen) {
        // Force re-render to recalculate position on window resize
        setIsCalendarOpen(false)
        setTimeout(() => setIsCalendarOpen(true), 10)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('resize', handleResize)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('resize', handleResize)
    }
  }, [onBlur, isCalendarOpen])

  // Recalculate position when calendar opens
  useEffect(() => {
    if (isCalendarOpen && inputRef.current) {
      // Calculate position and store in state
      const position = calculateCalendarPosition()
      setCalendarPosition(position)
    }
  }, [isCalendarOpen])

  // Calculate calendar position dynamically
  const calculateCalendarPosition = () => {
    if (!inputRef.current) return {}

    // Use stored rect or get current rect
    const inputRect = initialInputRect || inputRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const calendarHeight = 350 // Approximate calendar height
    const calendarWidth = Math.max(inputRect.width, 320) // Minimum width of 320px

    const spaceBelow = viewportHeight - inputRect.bottom
    const spaceAbove = inputRect.top

    // Determine if calendar should open upward or downward
    const openUpward = spaceBelow < calendarHeight && spaceAbove > spaceBelow

    // Calculate horizontal position to prevent overflow
    let leftPosition = inputRect.left
    if (leftPosition + calendarWidth > viewportWidth) {
      leftPosition = Math.max(20, viewportWidth - calendarWidth - 20)
    }

    // Ensure calendar doesn't go off-screen on the left
    leftPosition = Math.max(20, leftPosition)

    return {
      position: 'fixed' as const,
      // top: openUpward
      //   ? Math.max(20, inputRect.top - calendarHeight - 8)
      //   : Math.min(viewportHeight - calendarHeight - 20, inputRect.bottom + 8),
      // left: leftPosition,
      width: calendarWidth,
      maxWidth: Math.min(viewportWidth - 40, 400), // Maximum width with padding
      maxHeight: openUpward
        ? Math.min(spaceAbove - 20, calendarHeight)
        : Math.min(spaceBelow - 20, calendarHeight),
      zIndex: 100000,
    }
  }

  // Format date to Indonesian format (e.g., "4 November 1973")
  const formatDateToIndonesian = (dateString: string) => {
    if (!dateString) return ''

    const months = [
      'Januari',
      'Februari',
      'Maret',
      'April',
      'Mei',
      'Juni',
      'Juli',
      'Agustus',
      'September',
      'Oktober',
      'November',
      'Desember',
    ]

    const date = new Date(dateString)
    const day = date.getDate()
    const month = months[date.getMonth()]
    const year = date.getFullYear()

    return `${day} ${month} ${year}`
  }

  // Format date to YYYY-MM-DD format
  const formatDateToInput = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Handle calendar date selection
  const handleDateSelect = (selectedValue: any) => {
    if (selectedValue instanceof Date) {
      const formattedDate = formatDateToInput(selectedValue)
      onChange?.(formattedDate)
      setIsCalendarOpen(false)
      onBlur?.()
    }
  }

  // Handle input click
  const handleInputClick = () => {
    if (!disabled) {
      if (!isCalendarOpen) {
        // Store the initial input rect before any state changes
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect()
          setInitialInputRect(rect)

          // Use the stored rect to calculate position
          setTimeout(() => {
            const position = calculateCalendarPosition()
            setCalendarPosition(position)
          }, 0)
        }
      } else {
        // Clear stored rect when closing
        setInitialInputRect(null)
      }
      setIsCalendarOpen(!isCalendarOpen)
      onFocus?.()
    }
  }

  return (
    <>
      <div className={`fv-row ${className}`}>
        {label && (
          <label className='d-flex align-items-center fs-6 fw-semibold mb-2'>
            <span className={`${required ? 'required' : ''}`}>{label}</span>
          </label>
        )}

        <div className='position-relative' ref={calendarRef}>
          <div
            className={`date-input-wrapper ${isCalendarOpen ? 'calendar-open' : ''}`}
            ref={inputRef}
          >
            <div
              className={`input-group ${disabled ? 'opacity-50' : ''} ${
                error ? 'border-danger' : ''
              }`}
              onClick={handleInputClick}
            >
              <span className='input-group-text'>
                <i className='fas fa-calendar-alt'></i>
              </span>

              <input
                type='text'
                className='form-control form-control-lg'
                placeholder={placeholder}
                value={value ? formatDateToIndonesian(value) : ''}
                readOnly
                disabled={disabled}
                style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              />
            </div>
          </div>

          {/* React Calendar Dropdown */}
          {isCalendarOpen && !disabled && (
            <div className='calendar-dropdown-modal' style={calendarPosition}>
              <div
                style={{
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(5, 155, 182, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
                  background: '#ffffff',
                  border: '1px solid rgba(5, 155, 182, 0.1)',
                  overflow: 'hidden',
                  animation: 'slideDown 0.3s ease-out',
                  maxHeight: 'inherit',
                  width: '100%',
                }}
              >
                <Calendar
                  onChange={handleDateSelect}
                  value={value ? new Date(value) : null}
                  maxDate={maxDate}
                  minDate={minDate}
                  locale='id-ID'
                  className='custom-calendar-modal'
                />
              </div>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className='form-text text-danger mt-2'>
            <i className='fas fa-exclamation-circle me-1'></i>
            {error}
          </div>
        )}

        {/* Enhanced help text with animation */}
        {!error && (
          <div className='form-text text-muted mt-2'>
            <i className='fas fa-info-circle me-1'></i>
            Klik untuk membuka kalender dan pilih tanggal
          </div>
        )}

        {/* Visual date display */}
        {/* {value && !error && (
          <div
            className='mt-3 p-3 rounded-3 bg-light-success border border-success border-opacity-25 animate__animated animate__slideInUp'
            style={{
              background:
                'linear-gradient(135deg, rgba(5, 155, 182, 0.05) 0%, rgba(4, 122, 147, 0.05) 100%)',
            }}
          >
            <div className='d-flex align-items-center justify-content-between'>
              <div className='d-flex align-items-center'>
                <div
                  className='me-3 d-flex align-items-center justify-content-center'
                  style={{
                    width: '40px',
                    height: '40px',
                    background: 'linear-gradient(135deg, #059BB6 0%, #047a93 100%)',
                    borderRadius: '50%',
                    boxShadow: '0 4px 12px rgba(5, 155, 182, 0.3)',
                  }}
                >
                  <i className='fas fa-calendar-check text-white fs-5'></i>
                </div>
                <div>
                  <div className='fs-7 text-muted fw-medium mb-1'>{label} Dipilih</div>
                  <div className='fs-6 fw-bold text-dark'>{formatDateToIndonesian(value)}</div>
                </div>
              </div>
              {!disabled && (
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary btn-icon'
                  onClick={() => setIsCalendarOpen(true)}
                  style={{
                    borderRadius: '8px',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <i className='fas fa-edit fs-7'></i>
                </button>
              )}
            </div>
          </div>
        )} */}
      </div>

      {/* Calendar Styles */}
      <style>{`
        /* Enhanced Calendar UI Styles */
        .date-input-wrapper {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          isolation: isolate;
        }
        
        .date-input-wrapper:not(.calendar-open):hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 25px rgba(5, 155, 182, 0.15) !important;
        }
        
        .date-input-wrapper:not(.calendar-open):hover .input-group {
          box-shadow: 0 8px 25px rgba(5, 155, 182, 0.15) !important;
        }
        
        /* Completely disable all effects when calendar is open */
        .date-input-wrapper.calendar-open {
          box-shadow: 0 8px 25px rgba(5, 155, 182, 0.15) !important;
          transform: none !important;
          transition: none !important;
          position: relative !important;
        }
        
        .date-input-wrapper.calendar-open .input-group {
          box-shadow: 0 8px 25px rgba(5, 155, 182, 0.15) !important;
        }
        
        .date-input-wrapper.calendar-open:hover {
          transform: none !important;
          transition: none !important;
          box-shadow: 0 8px 25px rgba(5, 155, 182, 0.15) !important;
        }
        
        .date-input-wrapper.calendar-open:hover .input-group {
          transform: none !important;
          transition: none !important;
        }
        
        .date-input-wrapper.calendar-open *,
        .date-input-wrapper.calendar-open *:hover,
        .date-input-wrapper.calendar-open *:focus {
          transform: none !important;
          transition: none !important;
        }
        
        /* Badge animations */
        .badge {
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        
        /* Date display card animations */
        .animate__slideInUp {
          animation: slideInUp 0.6s ease-out;
        }
        
        .animate__fadeIn {
          animation: fadeIn 0.8s ease-out;
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translate3d(0, 30px, 0);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Gradient button effects */
        .btn-light-primary {
          background: linear-gradient(135deg, rgba(5, 155, 182, 0.1) 0%, rgba(4, 122, 147, 0.1) 100%);
          border: 1px solid rgba(5, 155, 182, 0.2);
          transition: all 0.3s ease;
        }
        
        .btn-light-primary:hover {
          background: linear-gradient(135deg, rgba(5, 155, 182, 0.2) 0%, rgba(4, 122, 147, 0.2) 100%);
          border-color: rgba(5, 155, 182, 0.4);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(5, 155, 182, 0.2);
        }
        
        /* Enhanced shadow effects */
        .shadow-sm {
          box-shadow: 0 2px 4px rgba(0,0,0,0.04), 0 4px 8px rgba(0,0,0,0.06) !important;
        }
        
        /* Success state styling */
        .bg-light-success {
          background-color: rgba(4, 180, 64, 0.05) !important;
        }
        
        .text-success {
          color: #04B440 !important;
        }
        
        .border-success {
          border-color: rgba(4, 180, 64, 0.2) !important;
        }

        /* React Calendar Custom Styles */
        .custom-calendar-modal {
          width: 100%;
          background: white;
          border: none;
          font-family: inherit;
          line-height: 1.125em;
          font-size: 14px;
        }
        
        .custom-calendar-modal,
        .custom-calendar-modal *,
        .custom-calendar-modal *:before,
        .custom-calendar-modal *:after {
          -moz-box-sizing: border-box;
          -webkit-box-sizing: border-box;
          box-sizing: border-box;
        }
        
        .custom-calendar-modal button {
          margin: 0;
          border: 0;
          outline: none;
          background: none;
          font-size: inherit;
          font-family: inherit;
          border-radius: 6px;
          transition: all 0.15s ease;
          cursor: pointer;
        }
        
        .custom-calendar-modal button:enabled:hover,
        .custom-calendar-modal button:enabled:focus {
          background-color: rgba(5, 155, 182, 0.1) !important;
          color: #059BB6 !important;
          transform: none !important;
          box-shadow: none !important;
        }
        
        .custom-calendar-modal button:disabled {
          background-color: #f0f0f0 !important;
          color: #999 !important;
          cursor: not-allowed;
        }
        
        .custom-calendar-modal .react-calendar__navigation {
          display: flex;
          height: 50px;
          margin-bottom: 0;
          background: linear-gradient(135deg, #059BB6 0%, #047a93 100%);
          padding: 0 15px;
        }
        
        .custom-calendar-modal .react-calendar__navigation button {
          min-width: 36px;
          background: none;
          color: white !important;
          font-size: 14px;
          font-weight: 600;
          border-radius: 6px;
          margin: 6px 2px;
          transition: background-color 0.15s ease;
        }
        
        .custom-calendar-modal .react-calendar__navigation button:enabled:hover,
        .custom-calendar-modal .react-calendar__navigation button:enabled:focus {
          background-color: rgba(255, 255, 255, 0.15) !important;
          color: white !important;
          transform: none !important;
        }
        
        .custom-calendar-modal .react-calendar__navigation__label {
          flex-grow: 1;
          font-size: 16px;
          font-weight: 700;
          text-align: center;
          color: white !important;
        }
        
        .custom-calendar-modal .react-calendar__month-view__weekdays {
          text-align: center;
          text-transform: uppercase;
          font-weight: bold;
          font-size: 0.7em;
          background-color: #f8f9fa;
          padding: 10px 0;
          border-bottom: 1px solid #e9ecef;
        }
        
        .custom-calendar-modal .react-calendar__month-view__weekdays__weekday {
          padding: 0.4em;
          color: #6c757d;
        }
        
        .custom-calendar-modal .react-calendar__month-view__weekNumbers .react-calendar__tile {
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7em;
          font-weight: bold;
          background: #f8f9fa;
          color: #6c757d;
        }
        
        .custom-calendar-modal .react-calendar__month-view__days__day--weekend {
          color: #dc3545 !important;
        }
        
        .custom-calendar-modal .react-calendar__month-view__days__day--neighboringMonth {
          color: #999 !important;
        }
        
        .custom-calendar-modal .react-calendar__year-view .react-calendar__tile,
        .custom-calendar-modal .react-calendar__decade-view .react-calendar__tile,
        .custom-calendar-modal .react-calendar__century-view .react-calendar__tile {
          padding: 1.5em 0.4em;
          font-weight: 600;
        }
        
        .custom-calendar-modal .react-calendar__tile {
          max-width: 100%;
          padding: 12px 4px;
          background: none;
          text-align: center;
          line-height: 14px;
          font-weight: 500;
          position: relative;
          color: #333;
          border: 1px solid transparent;
        }
        
        .custom-calendar-modal .react-calendar__tile:enabled:hover,
        .custom-calendar-modal .react-calendar__tile:enabled:focus {
          background-color: rgba(5, 155, 182, 0.1) !important;
          color: #059BB6 !important;
          font-weight: 600;
          border-color: rgba(5, 155, 182, 0.2);
          transform: none !important;
          box-shadow: none !important;
        }
        
        .custom-calendar-modal .react-calendar__tile--now {
          background: linear-gradient(135deg, rgba(5, 155, 182, 0.1) 0%, rgba(4, 122, 147, 0.1) 100%) !important;
          color: #059BB6 !important;
          font-weight: 600;
          border: 1px solid rgba(5, 155, 182, 0.3) !important;
        }
        
        .custom-calendar-modal .react-calendar__tile--now:enabled:hover,
        .custom-calendar-modal .react-calendar__tile--now:enabled:focus {
          background: linear-gradient(135deg, rgba(5, 155, 182, 0.2) 0%, rgba(4, 122, 147, 0.2) 100%) !important;
          color: #047a93 !important;
          border-color: rgba(5, 155, 182, 0.4) !important;
        }
        
        .custom-calendar-modal .react-calendar__tile--hasActive {
          background: linear-gradient(135deg, #059BB6 0%, #047a93 100%) !important;
          color: white !important;
          font-weight: 700;
          border-color: #059BB6 !important;
        }
        
        .custom-calendar-modal .react-calendar__tile--hasActive:enabled:hover,
        .custom-calendar-modal .react-calendar__tile--hasActive:enabled:focus {
          background: linear-gradient(135deg, #047a93 0%, #036b7a 100%) !important;
          color: white !important;
          border-color: #047a93 !important;
        }
        
        .custom-calendar-modal .react-calendar__tile--active {
          background: linear-gradient(135deg, #059BB6 0%, #047a93 100%) !important;
          color: white !important;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(5, 155, 182, 0.3) !important;
          border-color: #059BB6 !important;
        }
        
        .custom-calendar-modal .react-calendar__tile--active:enabled:hover,
        .custom-calendar-modal .react-calendar__tile--active:enabled:focus {
          background: linear-gradient(135deg, #047a93 0%, #036b7a 100%) !important;
          color: white !important;
          border-color: #047a93 !important;
        }
        
        .custom-calendar-modal .react-calendar--selectRange .react-calendar__tile--hover {
          background-color: rgba(5, 155, 182, 0.1) !important;
        }
        
        /* Modal-specific positioning */
        .calendar-dropdown-modal {
          position: fixed;
          background: rgba(0, 0, 0, 0.01);
          backdrop-filter: blur(0.5px);
        }
        
        /* Responsive improvements */
        @media (max-width: 768px) {
          .date-input-wrapper {
            margin-bottom: 1rem;
          }
          
          .input-group-text {
            min-width: 50px !important;
          }
          
          .date-input-field {
            font-size: 14px !important;
            padding-left: 15px !important;
            padding-right: 15px !important;
          }
          
          .custom-calendar {
            font-size: 14px;
          }
          
          .custom-calendar .react-calendar__tile {
            padding: 12px 4px;
          }
          
          .calendar-dropdown {
            left: -10px !important;
            right: -10px !important;
          }
        }
      `}</style>
    </>
  )
}

export default CustomCalendar
